import { useEffect, useRef, useState } from 'react'
import { MapPin, Link2, Search, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import {
  suggestAddresses,
  saveAddress,
  type AddressSuggestion,
} from '../../utils/addressTools'

export function EditAddressDialog({
  driverId,
  driverName,
  label,
  buttonClassName,
  onSaved,
}: {
  driverId: string
  driverName: string | null
  label: string
  buttonClassName?: string
  onSaved: () => void
}) {
  const [open, setOpen] = useState(false)

  // Search-address state.
  const [search, setSearch] = useState('')
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [suggesting, setSuggesting] = useState(false)
  const [placeId, setPlaceId] = useState<string | null>(null)

  // Paste-a-link state.
  const [mapLink, setMapLink] = useState('')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Guards so a stale suggestion response never overwrites a picked value.
  const pickedRef = useRef(false)

  function reset() {
    setSearch('')
    setSuggestions([])
    setSuggesting(false)
    setPlaceId(null)
    setMapLink('')
    setSaving(false)
    setError(null)
    pickedRef.current = false
  }

  // Debounced autocomplete: only call when >= 3 chars and the user is typing
  // (not immediately after picking a suggestion).
  useEffect(() => {
    if (!open) return
    if (pickedRef.current) return
    const q = search.trim()
    if (q.length < 3) {
      setSuggestions([])
      setSuggesting(false)
      return
    }
    const controller = new AbortController()
    setSuggesting(true)
    const t = setTimeout(async () => {
      try {
        const results = await suggestAddresses(q, controller.signal)
        if (!controller.signal.aborted) setSuggestions(results)
      } catch (err) {
        if (!controller.signal.aborted && (err as Error).name !== 'AbortError') {
          setSuggestions([])
        }
      } finally {
        if (!controller.signal.aborted) setSuggesting(false)
      }
    }, 300)
    return () => {
      controller.abort()
      clearTimeout(t)
    }
  }, [search, open])

  function pickSuggestion(s: AddressSuggestion) {
    pickedRef.current = true
    setPlaceId(s.place_id)
    setSearch(s.text)
    setSuggestions([])
    setMapLink('')
  }

  function onSearchChange(value: string) {
    pickedRef.current = false
    setPlaceId(null)
    setSearch(value)
  }

  async function handleSave() {
    setError(null)
    setSaving(true)
    try {
      // Priority: place_id (picked suggestion) > map_link (pasted) > raw text.
      const payload =
        placeId != null
          ? { driver_id: driverId, place_id: placeId }
          : mapLink.trim() !== ''
            ? { driver_id: driverId, map_link: mapLink.trim() }
            : { driver_id: driverId, address_text: search.trim() }

      const result = await saveAddress(payload)
      toast.success(`Address updated — ${result.geocode_status}`)
      setOpen(false)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const canSave =
    !saving && (placeId != null || mapLink.trim() !== '' || search.trim() !== '')

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) reset()
        setOpen(next)
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-7 gap-1.5 px-2 text-xs', buttonClassName)}
        >
          <MapPin className="h-3.5 w-3.5" />
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit driver address</DialogTitle>
          <DialogDescription>{driverName || 'Unnamed driver'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Search address */}
          <div className="space-y-1.5">
            <Label htmlFor="addr-search" className="flex items-center gap-1.5">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              Search address
            </Label>
            <div className="relative">
              <Input
                id="addr-search"
                autoComplete="off"
                placeholder="Start typing an address…"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              {suggesting && (
                <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
              {suggestions.length > 0 && (
                <ul className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md">
                  {suggestions.map((s) => (
                    <li key={s.place_id}>
                      <button
                        type="button"
                        onClick={() => pickSuggestion(s)}
                        className="flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground"
                      >
                        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span>{s.text}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {placeId != null && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                Suggestion selected.
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            or
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Paste a map link */}
          <div className="space-y-1.5">
            <Label htmlFor="addr-link" className="flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
              Paste a map link
            </Label>
            <Input
              id="addr-link"
              autoComplete="off"
              placeholder="Google Maps, Waze, or Apple Maps link"
              value={mapLink}
              onChange={(e) => {
                setMapLink(e.target.value)
                if (e.target.value.trim() !== '') {
                  setPlaceId(null)
                  pickedRef.current = false
                }
              }}
            />
          </div>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving && <Spinner className="mr-1.5 h-4 w-4" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
